import React, { useEffect, useRef, useState } from 'react';
import type { MackTurn } from '../lib/mackCurrentTurn';

import {
  LaneImages, laneImagePlan, laneTaker, hasAttachmentPlaceholder, renderAttachmentLine,
  type LaneImageRef,
} from './chat/LaneImages';

export interface Message {
  id: string;
  mackTurn?: MackTurn;
  // RECITE-WIRING-DELTA — 'system' is the INTERFACE voice: errors/statuses the
  // UI reports itself. No source-authored string is ever attributed to Rexy or
  // an agent; those render through the system-notice branch, never her bubble.
  sender: 'user' | 'rexy' | 'system';
  text: string;
  timestamp: string;
  source?: string;
  memoryStatus?: 'saved' | 'blocked' | 'override' | null;
  // System notices only — 'error' gets the danger accent strip, 'status' the
  // muted strip. Ignored on user/rexy messages.
  severity?: 'error' | 'status';
  // F1 Build B — non-empty only when local (Qwen) was down and the backend fell
  // back to the API to keep the OS alive. Rendered as a subtle amber inline notice.
  fallbackNotice?: string;
  // Frontier lanes (Phase 4) — the images a frontier turn carried or made: the
  // reply's `images`, or a history row's. Absent on every other message.
  images?: LaneImageRef[];
}

interface ChatWindowProps {
  messages: Message[];
  activeAgent?: string | null;
  authToken?: string | null;
  // CI-6: iOS-shape bubble accents (domain-driven). ChatSurface computes them
  // from lib/agents.ts using activeDomain and passes through. Optional so the
  // component still renders defensively if not provided (neutral fallback).
  accentColor?: string;
  accentMuted?: string;
}

function SourceBadge({ source, memoryStatus }: { source?: string; memoryStatus?: string | null }) {
  if (!source) return null;

  const config: Record<string, { label: string; className: string }> = {
    LOCAL_AI:        { label: '▸ LOCAL AI',            className: 'text-green-500/80' },
    LOCAL_AI_MEMORY: { label: '▸ LOCAL AI  ·  memory', className: 'text-green-500/80' },
    CLAUDE:          { label: '▸ CLAUDE API',           className: 'text-blue-400/80'  },
    PERPLEXITY:      { label: '▸ PERPLEXITY',           className: 'text-purple-400/80'},
    OPENAI:          { label: '▸ GPT-5.5',              className: 'text-emerald-400/80'},
    SUB_CLAUDE:      { label: '▸ CLAUDE · SUB',         className: 'text-blue-400/80'  },
    SUB_GPT:         { label: '▸ GPT · SUB',            className: 'text-emerald-400/80'},
    JOINT:           { label: '▸ SPARTICUS · GHOST',    className: 'text-amber-400/80' },
    CODEX:           { label: '▸ CODEX',                className: 'text-yellow-400/80'},
    NANOBANANA:      { label: '▸ NANO BANANA',          className: 'text-pink-400/80'  },
  };

  const cfg = config[source] || { label: `▸ ${source}`, className: 'text-rexy-muted/70' };
  const isMemory = source === 'LOCAL_AI_MEMORY';

  // Memory dot badge config
  const memDot: Record<string, { color: string; label: string }> = {
    saved:    { color: 'bg-green-500',  label: '●SAVED'    },
    blocked:  { color: 'bg-red-500',    label: '●BLOCKED'  },
    override: { color: 'bg-orange-400', label: '●OVERRIDE' },
  };
  const dot = memoryStatus ? memDot[memoryStatus] : null;

  return (
    <div className="flex items-center gap-2 mb-2">
      <span className={`text-[9px] font-bold uppercase tracking-widest ${cfg.className}`}>
        {isMemory ? '▸ LOCAL AI' : cfg.label}
      </span>
      {isMemory && (
        <span className="text-[8px] font-bold uppercase tracking-widest
          bg-rexy-border text-rexy-muted px-1.5 py-0.5 rounded">
          memory
        </span>
      )}
      {dot && (
        <span className={`flex items-center gap-1 text-[8px] font-bold
          uppercase tracking-widest ${
          memoryStatus === 'saved'    ? 'text-green-500/80'  :
          memoryStatus === 'blocked'  ? 'text-red-400/80'    :
          'text-orange-400/80'
        }`}>
          <span className={`w-1.5 h-1.5 rounded-full ${dot.color} inline-block`} />
          {dot.label}
        </span>
      )}
    </div>
  );
}

function renderInline(text: string): React.ReactNode {
  // Bold: **text** or __text__
  const parts = text.split(/(\*\*[^*]+\*\*|__[^_]+__)/g);
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={i} className="font-semibold text-rexy-text">{part.slice(2, -2)}</strong>;
    }
    if (part.startsWith('__') && part.endsWith('__')) {
      return <strong key={i} className="font-semibold text-rexy-text">{part.slice(2, -2)}</strong>;
    }
    return part;
  });
}

const ChatWindow: React.FC<ChatWindowProps> = ({
  messages,
  activeAgent,
  authToken,
  // CI-6: iOS bubble accents. Neutral fallback if not provided.
  accentColor = '#d4d4d4',
  accentMuted = '#404040',
}) => {
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleDownloadFile = async (filename: string, fileContent: string) => {
    try {
      // Protocol-keyed same-origin base (App.tsx pattern): HTTPS → page host no port,
      // HTTP → Flask :5000 direct (LAN path unchanged).
      const API_BASE = window.location.protocol === 'https:'
        ? `https://${window.location.host}/api`
        : `${window.location.protocol}//${window.location.hostname}:5000/api`;
      const res = await fetch(`${API_BASE}/download`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': authToken ? `Bearer ${authToken}` : '',
        },
        body: JSON.stringify({ filename, content: fileContent }),
      });
      if (res.ok) {
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        a.click();
        URL.revokeObjectURL(url);
      } else {
        console.error('Download failed:', res.status);
      }
    } catch (e) {
      console.error('Download failed:', e);
    }
  };

  if (messages.length === 0) {
    // CI-6: empty-state root is transparent so the atmosphere bleeds through.
    return (
      <div className="flex-1 flex flex-col items-center justify-center
        text-rexy-muted select-none">
        <div className="w-14 h-14 bg-rexy-card rounded-full flex items-center
          justify-center text-2xl font-bold text-rexy-text mb-4 border
          border-rexy-border">
          R
        </div>
        <p className="text-sm font-medium text-rexy-muted">Rexy is ready.</p>
        <p className="text-xs mt-1 text-rexy-muted/60">Select an agent and type a goal.</p>
      </div>
    );
  }

  // CI-6: main scroll container transparent; iOS shape relies on the
  // atmosphere bleeding through between/around bubbles.
  return (
    <div className="flex-1 overflow-y-auto px-6 py-4 gap-5 flex flex-col">
      {messages.map((message) => {
        // Frontier lanes (Phase 4): null for a message without `images` — then
        // nothing below renders differently.
        const lanePlan = laneImagePlan(message);
        // Content rendering — preserved verbatim from the pre-CI-6 implementation.
        // Only the surrounding bubble JSX changes; the segments parser, markdown
        // rules, code-block UI, preview/download/PDF handlers stay identical.
        const contentBlock = (
          <div className="prose-rexy">
            {(() => {
              // Pre-process: split text into segments (code blocks vs prose)
              const segments: Array<{ type: 'prose' | 'code'; content: string; lang?: string; filename?: string }> = [];
              const lines = message.text.split('\n');
              let i = 0;
              while (i < lines.length) {
                const fenceMatch = lines[i].match(/^```(\S*)?(?:\s+(.+))?/);
                if (fenceMatch) {
                  const lang = fenceMatch[1] || '';
                  const filename = fenceMatch[2]?.trim() || undefined;
                  const codeLines: string[] = [];
                  i++;
                  while (i < lines.length && !lines[i].startsWith('```')) {
                    codeLines.push(lines[i]);
                    i++;
                  }
                  i++; // skip closing ```
                  segments.push({ type: 'code', content: codeLines.join('\n'), lang, filename });
                } else {
                  // Collect prose lines until next code fence
                  const proseLines: string[] = [];
                  while (i < lines.length && !lines[i].match(/^```/)) {
                    proseLines.push(lines[i]);
                    i++;
                  }
                  segments.push({ type: 'prose', content: proseLines.join('\n') });
                }
              }

              const laneTake = lanePlan ? laneTaker(lanePlan) : null;
              return segments.map((seg, si) => {
                if (seg.type === 'code') {
                  // Infer filename from lang if not explicit
                  // e.g. ```tsx SecurityPage.tsx  or  ```python ceo.py
                  const displayName = seg.filename || (seg.lang ? `code.${seg.lang}` : 'code');
                  const isFile = !!(seg.filename || (seg.lang && ['tsx','jsx','ts','js','py','json','css','html','yaml','yml','sh','md'].includes(seg.lang)));
                  const isPreviewable = ['tsx','jsx','html'].includes(seg.lang || '');

                  const triggerPreview = () => {
                    window.dispatchEvent(new CustomEvent('rexy-preview', {
                      detail: { code: seg.content, filename: displayName, lang: seg.lang }
                    }));
                  };

                  return (
                    <div key={si} className="my-3 rounded-lg border border-rexy-border overflow-hidden">
                      <div className="flex items-center justify-between px-3 py-1.5 bg-[#1a1a1a] border-b border-rexy-border">
                        <span className="text-[9px] font-bold uppercase tracking-widest text-rexy-muted/70">
                          {seg.lang || 'code'}{seg.filename ? ` · ${seg.filename}` : ''}
                        </span>
                        <div className="flex items-center gap-3">
                          {isPreviewable && (
                            <button
                              onClick={triggerPreview}
                              className="text-[9px] font-bold uppercase tracking-widest text-purple-400/70 hover:text-purple-400 transition-colors flex items-center gap-1"
                              title="Preview in CODE workspace"
                            >
                              ◈ preview
                            </button>
                          )}
                          {isFile && (
                            <button
                              onClick={() => handleDownloadFile(displayName, seg.content)}
                              className="text-[9px] font-bold uppercase tracking-widest text-blue-400/70 hover:text-blue-400 transition-colors flex items-center gap-1"
                              title={`Download ${displayName}`}
                            >
                              ↓ download
                            </button>
                          )}
                        </div>
                      </div>
                      <pre className="text-xs text-rexy-text/90 p-3 overflow-x-auto leading-relaxed font-mono bg-[#111]">
                        <code>{seg.content}</code>
                      </pre>
                    </div>
                  );
                }

                // Prose segment — line by line rendering
                return (
                  <div key={si}>
                    {seg.content.split('\n').map((line, li) => {
                      if (line.startsWith('### ')) return <h3 key={li} className="text-sm font-bold text-rexy-text mt-3 mb-1">{line.slice(4)}</h3>;
                      if (line.startsWith('## ')) return <h2 key={li} className="text-sm font-bold text-rexy-text mt-3 mb-1 uppercase tracking-wide">{line.slice(3)}</h2>;
                      if (line.startsWith('# ')) return <h1 key={li} className="text-base font-bold text-rexy-text mt-3 mb-1">{line.slice(2)}</h1>;
                      if (line.trim() === '---' || line.trim() === '───') return <hr key={li} className="border-rexy-border my-2" />;
                      if (line.trim() === '--- SPARTICUS ---') return (
                        <div key={li} className="flex items-center gap-2 my-2">
                          <span className="text-[9px] font-bold uppercase tracking-widest text-purple-400/80">▸ SPARTICUS · Claude</span>
                          <div className="flex-1 h-px bg-purple-500/20" />
                        </div>
                      );
                      if (line.trim() === '--- GHOST ---') return (
                        <div key={li} className="flex items-center gap-2 my-3">
                          <div className="flex-1 h-px bg-emerald-500/20" />
                          <span className="text-[9px] font-bold uppercase tracking-widest text-emerald-400/80">GHOST · GPT-5.5 ▸</span>
                        </div>
                      );
                      if (line.trim() === '') return <div key={li} className="h-2" />;
                      if (line.startsWith('• ') || line.startsWith('- ') || line.startsWith('* ')) {
                        const t = line.slice(2);
                        return <div key={li} className="flex gap-2 text-sm leading-relaxed"><span className="text-rexy-muted mt-0.5">•</span><span>{renderInline(t)}</span></div>;
                      }
                      const numMatch = line.match(/^(\d+)[\.\)] (.+)/);
                      if (numMatch) return <div key={li} className="flex gap-2 text-sm leading-relaxed"><span className="text-rexy-muted min-w-[1rem]">{numMatch[1]}.</span><span>{renderInline(numMatch[2])}</span></div>;
                      if (laneTake && hasAttachmentPlaceholder(line)) {
                        return <p key={li} className="text-sm leading-relaxed">{renderAttachmentLine(line, laneTake, renderInline)}</p>;
                      }
                      return <p key={li} className="text-sm leading-relaxed">{renderInline(line)}</p>;
                    })}
                  </div>
                );
              });
            })()}
          </div>
        );

        // Footer (timestamp only). B6 W0 (owner ruling 2026-07-11): the PDF export
        // path is cut — the transcript is the chat thread; no document, no download.
        const footer = (
          <div className="flex items-center gap-2 mt-1 px-1">
            <span className="text-[10px] text-rexy-muted/50">{message.timestamp}</span>
          </div>
        );

        // ── SYSTEM notice — the INTERFACE speaking, never Rexy/an agent ──────
        // RECITE-WIRING-DELTA: visually distinct from both bubble idioms — a
        // muted bordered notice row on a recessed plate (derived dark value,
        // same family as the code-block chrome above), left accent strip
        // (danger for errors, muted for status), no avatar, no agent name, no
        // SourceBadge. Dimensional per the locked theme: soft shadow, not flat.
        if (message.sender === 'system') {
          const isError = message.severity !== 'status';
          return (
            <div key={message.id} className="flex flex-col w-full max-w-[92%] mx-auto">
              <div className="flex w-full rounded-lg border border-rexy-border bg-[#101010] shadow-sm overflow-hidden">
                <div
                  className={`w-1 self-stretch ${isError ? 'bg-rexy-danger/70' : 'bg-rexy-muted/40'}`}
                  aria-hidden
                />
                <div className="flex-1 px-3.5 py-2.5">
                  <span className={`text-[9px] font-bold uppercase tracking-widest block mb-1 ${
                    isError ? 'text-rexy-danger/80' : 'text-rexy-muted/80'
                  }`}>
                    {isError ? '▸ SYSTEM · ERROR' : '▸ SYSTEM'}
                  </span>
                  <p className="text-sm leading-relaxed text-rexy-muted whitespace-pre-wrap">
                    {message.text}
                  </p>
                </div>
              </div>
              {footer}
            </div>
          );
        }

        // ── User bubble — iOS shape: solid accent-muted fill, accent border ──
        if (message.sender === 'user') {
          return (
            <div key={message.id} className="flex flex-col max-w-[80%] ml-auto items-end pl-12">
              <div
                className="px-3.5 py-2.5 rounded-2xl border text-sm leading-relaxed text-rexy-text"
                style={{ backgroundColor: accentMuted, borderColor: accentColor }}
              >
                {contentBlock}
                {lanePlan && <LaneImages images={lanePlan.rest} />}
              </div>
              {footer}
            </div>
          );
        }

        // ── Agent bubble — iOS shape: accent strip + raised surface. (The old
        // blocked-bubble variant is CUT — governance blocks render as SYSTEM
        // notices, never an agent bubble; Rule One close-out, owner-ruled.) ──
        return (
          <div key={message.id} className="flex flex-col max-w-[80%] mr-auto pr-12"
            {...(activeAgent?.toUpperCase() === 'MACK'
              && message.mackTurn?.domain === 'TRADING' && message.mackTurn.agent === 'MACK'
              && message.mackTurn.current_turn_response ? {
                'data-request-id': message.mackTurn.request_id,
                'data-trace-id': message.mackTurn.trace_id,
                'data-current-turn-response': 'true',
                ...(message.mackTurn.synthetic_status ? {
                  'data-evidence-class': 'SYNTHETIC_NON_OPERATIONAL',
                  'data-job-id': message.mackTurn.synthetic_status.job_id,
                  'data-worker-identity': message.mackTurn.synthetic_status.worker_identity,
                  'data-state': message.mackTurn.synthetic_status.state,
                  'data-prompt-sha256': message.mackTurn.synthetic_status.prompt_sha256,
                  'data-capture-sha256': message.mackTurn.synthetic_status.capture_sha256,
                  'data-response-sha256': message.mackTurn.synthetic_status.response_sha256,
                } : {}),
              } : {})}
          >
            <div className="flex w-full">
              <div
                className="w-0.5 rounded-full self-stretch mr-2 opacity-70"
                style={{ backgroundColor: accentColor }}
                aria-hidden
              />
              <div className="flex-1 px-3.5 py-2.5 rounded-xl border text-sm leading-relaxed shadow-md bg-rexy-card text-rexy-text border-rexy-border">
                <SourceBadge source={message.source} memoryStatus={message.memoryStatus} />
                {/* F1 Build B — fallback notice: local (Qwen) was down, answered via API. */}
                {message.fallbackNotice && (
                  <span
                    className="inline-flex items-center gap-1.5 mb-2 px-2 py-0.5 rounded-full
                      text-[9px] font-bold uppercase tracking-widest text-rexy-warning
                      border border-rexy-warning/30 bg-rexy-warning/10"
                    title={message.fallbackNotice}
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-rexy-warning/80" />
                    {message.fallbackNotice}
                  </span>
                )}
                {contentBlock}
                {lanePlan && <LaneImages images={lanePlan.rest} />}
              </div>
            </div>
            {footer}
          </div>
        );
      })}
      <div ref={bottomRef} />
    </div>
  );
};

export default ChatWindow;
